import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import type { LedgerAccount, JournalEntry } from '../types';
import { addMissingSystemAccounts } from '../utils/ledger';

// Shared live read of the two ledger collections for the Accounting report
// pages (Trial Balance, General Ledger, Balance Sheet, Profit & Loss, Journal
// Entries). Extracted rather than copied 5× — same reasoning as ReturnModal.
export function useLedgerData() {
  const { business } = useAuth();
  const [accounts, setAccounts] = useState<LedgerAccount[]>([]);
  const [entries,  setEntries]  = useState<JournalEntry[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;   // ProtectedRoute guarantees a business before this renders
    let gotA = false, gotE = false, repaired = false;
    const settle = () => { if (gotA && gotE) setLoading(false); };
    const u1 = onSnapshot(
      collection(db, 'ledgerAccounts'),
      snap => {
        setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as LedgerAccount))); gotA = true; settle();
        // Chart already set up but missing a system account added since
        // (e.g. Sales Expenses) — add it once, quietly. An empty chart is
        // left alone: that first-time setup stays an explicit choice.
        if (!repaired && !snap.empty) {
          repaired = true;
          addMissingSystemAccounts(db, business.id, new Set(snap.docs.map(d => d.id))).catch(() => {});
        }
      },
      () => { gotA = true; settle(); },
    );
    const u2 = onSnapshot(
      collection(db, 'journalEntries'),
      snap => { setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() } as JournalEntry))); gotE = true; settle(); },
      () => { gotE = true; settle(); },
    );
    return () => { u1(); u2(); };
  }, [business]);

  return { accounts, entries, loading };
}
